import { useLayoutEffect, useState } from "react";
import {
  FLOATING_CHAT_MIN_HEIGHT_PX,
  FLOATING_CHAT_REGION_VERTICAL_INSET_PX,
} from "./floatingChatShell";

export function fallbackMaxPanelHeightPx(
  floorPx = FLOATING_CHAT_MIN_HEIGHT_PX,
) {
  if (typeof window === "undefined") return 560;
  return Math.max(floorPx, Math.floor(window.innerHeight - 80));
}

export function maxPanelHeightFromRegionEl(
  el: HTMLElement | null,
  floorPx = FLOATING_CHAT_MIN_HEIGHT_PX,
) {
  if (!el) return fallbackMaxPanelHeightPx(floorPx);
  return Math.max(
    floorPx,
    Math.floor(el.clientHeight - FLOATING_CHAT_REGION_VERTICAL_INSET_PX),
  );
}

/**
 * Max height (px) a floating chat window may grow to inside its content region:
 * the region's `clientHeight` less a vertical inset, ResizeObserver-tracked.
 * Height (unlike width) stays meaningful even while a full-width detail pane
 * squeezes the region, so this caps the left-window mode too. `floorPx` is the
 * least it reports however short the region is.
 */
export function useRegionMaxPanelHeight(
  el: HTMLElement | null,
  floorPx = FLOATING_CHAT_MIN_HEIGHT_PX,
): number {
  const [maxHeightPx, setMaxHeightPx] = useState(() =>
    fallbackMaxPanelHeightPx(floorPx),
  );
  useLayoutEffect(() => {
    const update = () => {
      setMaxHeightPx(maxPanelHeightFromRegionEl(el, floorPx));
    };
    update();
    if (!el) return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [el, floorPx]);
  return maxHeightPx;
}
